/**
 * Typed client for `/api/v1/admin/**`.
 *
 * This is the WHOLE API surface available to this app -- there is no
 * `/support/**` and no operator `/sites/**` fallback, because the
 * admin api-server (`NEXSOSS_API_ROLE=admin`) mounts only
 * `api/admin.py`'s router and returns 404 for everything else
 * (`main.py::create_app`). That is deliberate and it constrains the UI:
 * anything this console shows must come from these three endpoints or
 * be derived in the browser. Where a screen would benefit from data
 * this tier cannot see, it says so rather than reaching sideways.
 */
import { getCurrentToken } from '@shared/auth/token-store';
import { getRuntimeConfig } from '../config/runtime-config';

export class AdminApiError extends Error {
  constructor(
    public status: number,
    public code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { apiBaseUrl } = getRuntimeConfig();
  const token = getCurrentToken();
  const resp = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!resp.ok) {
    let code: string | undefined;
    let message = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      code = body?.detail?.code ?? body?.code;
      message = body?.detail?.message ?? body?.message ?? message;
    } catch {
      // non-JSON error body -- keep the generic message
    }
    throw new AdminApiError(resp.status, code, message);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// --- Types (mirror backend/api_server/api/admin.py) ---------------------------

/**
 * The join of "what the database says" with "what the bus says".
 * Every value except `live` and `retired` is a DRIFT state. Derived
 * server-side (`admin.py::_lifecycle_state`) rather than here, so the
 * classification is tested once and cannot disagree between the
 * console and any future CLI.
 */
export type LifecycleState =
  | 'live'
  | 'degraded'
  | 'configured_not_deployed'
  | 'incomplete_binding'
  | 'retired'
  | 'retired_still_publishing';

export type AdminWorkerStatus = 'ready' | 'initialising' | 'stale' | 'unreachable' | 'failed';

export interface AdminSiteBinding {
  deployment_manifest_version: string | null;
  worker_image_digest: string | null;
  zenoh_router_endpoint: string | null;
  platform_bus_endpoint: string | null;
  rmf_session_uri: string | null;
  site_worker_command_keyexpr_prefix: string | null;
  broker_endpoint: string | null;
  /** An env var NAME or secret path -- NEVER the token itself. The
   *  backend model enforces the same rule; the form label says so too,
   *  because this is the field most likely to be pasted a secret. */
  broker_token_ref: string | null;
  certified_capabilities: string[];
}

export interface AdminSite {
  site_id: string;
  label: string;
  timezone: string;
  /** Days a finished task stays in the live task-state cache (the
   *  operator console's Tasks tab). Server default 30. Optional only
   *  because an api-server one release behind does not echo it — the
   *  form falls back to the server defaults, which is what that
   *  server would apply anyway. */
  task_live_retention_days?: number;
  /** Days task_completions/task_history rows are kept in Postgres —
   *  the Metrics screen and the drawer Timeline. Server default 90;
   *  below 90 truncates the Metrics screen's longest window. */
  task_history_retention_days?: number;
  archived: boolean;
  archived_at_ms: number | null;
  binding: AdminSiteBinding | null;
  assigned_user_count: number;
  eligible_for_bus_acl: boolean;
  worker_status: AdminWorkerStatus;
  worker_epoch: number | null;
  last_heartbeat_ms: number | null;
  lifecycle_state: LifecycleState;
}

/** One `user_site_assignments` row, joined to a display name.
 *
 *  Every profile field is nullable and a null one is not an error to
 *  hide: assignments created by `seed_site.py` before the `known_users`
 *  directory existed have no matching row, and an admin must still see
 *  that the grant is there. Render `sub_suffix` when there is no name.
 *
 *  `sub_suffix` also disambiguates. Email is NOT unique here -- two
 *  distinct ZITADEL subs carrying one address is an observed state in
 *  this tenant (a re-registered account), so two rows can legitimately
 *  show the same email and must remain tellable apart. */
export interface AdminSiteAssignment {
  id: number;
  user_sub: string;
  sub_suffix: string;
  email: string | null;
  display_name: string | null;
  preferred_username: string | null;
  last_seen_ms: number | null;
  granted_by_sub: string;
  granted_by_email: string | null;
  granted_at_ms: number | null;
}

export interface CommissionSiteRequest {
  site_id: string;
  label: string;
  timezone: string;
  /** Backend clamps to 7-365; must be <= task_history_retention_days.
   *  Optional on the server (defaults 30/90) but always sent by the
   *  form, so a commission never silently resets a tuned value. */
  task_live_retention_days: number;
  /** Backend clamps to 30-730 and enforces >= task_live_retention_days. */
  task_history_retention_days: number;
  binding: Partial<AdminSiteBinding>;
}

export interface LifecycleResponse {
  site_id: string;
  action: 'commission' | 'decommission';
  applied: string[];
  /** Host commands the API deliberately cannot run (it holds neither
   *  `docker.sock` nor the CA key). A site is NOT commissioned, and a
   *  decommission has NOT revoked bus access, until these are run --
   *  which is why `PendingStepsBanner` persists them. */
  pending_operator_steps: string[];
  at_ms: number;
}

// --- Calls -------------------------------------------------------------------

export function listAdminSites(includeArchived = true): Promise<AdminSite[]> {
  return request(`/api/v1/admin/sites?include_archived=${includeArchived}`);
}

/** A searchable directory entry — someone who has signed in here.
 *
 *  `sub_count_for_email > 1` means this address is carried by more than
 *  one distinct principal in the same result set, which is a real state
 *  in this tenant. The picker must never let an admin choose on email
 *  alone when that is true. */
export interface AdminKnownUser {
  user_sub: string;
  sub_suffix: string;
  email: string | null;
  display_name: string | null;
  preferred_username: string | null;
  last_seen_ms: number | null;
  is_platform_admin: boolean;
  sub_count_for_email: number;
}

export function listSiteAssignments(siteId: string): Promise<AdminSiteAssignment[]> {
  return request(`/api/v1/admin/sites/${encodeURIComponent(siteId)}/assignments`);
}

export function searchKnownUsers(q: string): Promise<AdminKnownUser[]> {
  return request(`/api/v1/admin/users?q=${encodeURIComponent(q)}`);
}

export function createSiteAssignment(
  siteId: string,
  userSub: string,
  acknowledgeUnknownPrincipal = false,
): Promise<AdminSiteAssignment> {
  // The flag is only ever sent when the caller passed it explicitly.
  // The server rejects unknown fields (`extra="forbid"`), so a
  // misspelling here is a 422 rather than a silently-ignored
  // acknowledgement that leaves the grant refused for a reason the
  // operator already answered.
  return request(`/api/v1/admin/sites/${encodeURIComponent(siteId)}/assignments`, {
    method: 'POST',
    body: JSON.stringify({
      user_sub: userSub,
      ...(acknowledgeUnknownPrincipal ? { acknowledge_unknown_principal: true } : {}),
    }),
  });
}

export function deleteSiteAssignment(siteId: string, assignmentId: number): Promise<void> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(siteId)}/assignments/${assignmentId}`,
    { method: 'DELETE' },
  );
}

/** A site's shared alert destination — the mailbox or chat room its
 *  alerts reach regardless of who is on shift.
 *
 *  `config` arrives MASKED for a chat channel: the webhook URL is a
 *  bearer credential and never leaves the server, so the row shows only
 *  its last characters. An email channel's `to` list is an address
 *  book, not a secret, and comes back in full so the row can be
 *  checked. Editing a webhook therefore means deleting and re-adding
 *  it, which is why there is no update call. */
export interface AdminNotificationChannel {
  channel_id: string;
  site_id: string;
  kind: 'email' | 'chat';
  config: { to?: string[]; webhook_url?: string };
  min_severity: 'critical' | 'warning' | 'info';
  enabled: boolean;
  created_by_sub: string;
  created_at_ms: number;
}

export function listSiteChannels(siteId: string): Promise<AdminNotificationChannel[]> {
  return request(`/api/v1/admin/sites/${encodeURIComponent(siteId)}/channels`);
}

export function createSiteChannel(
  siteId: string,
  body: {
    kind: 'email' | 'chat';
    config: { to?: string[]; webhook_url?: string };
    min_severity: 'critical' | 'warning' | 'info';
  },
): Promise<AdminNotificationChannel> {
  return request(`/api/v1/admin/sites/${encodeURIComponent(siteId)}/channels`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteSiteChannel(siteId: string, channelId: string): Promise<void> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(siteId)}/channels/${encodeURIComponent(channelId)}`,
    { method: 'DELETE' },
  );
}

export function commissionSite(body: CommissionSiteRequest): Promise<LifecycleResponse> {
  return request('/api/v1/admin/sites/commission', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * `dryRun` has NO default here, unlike the backend (where it defaults
 * to true). A caller that forgets the argument should fail to compile
 * rather than quietly inherit a default in either direction -- the
 * backend's safe default protects curl; an explicit argument protects
 * this codebase from a refactor that silently flips a preview into a
 * purge.
 */
export function decommissionSite(siteId: string, dryRun: boolean): Promise<LifecycleResponse> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(siteId)}/decommission?dry_run=${dryRun}`,
    { method: 'POST' },
  );
}

/** One platform-wide key/value setting (`platform_settings` table).
 *
 *  `value` is what the database row holds — null means no row, i.e.
 *  "defer to the environment". `effective_value` is what the AI
 *  modules will actually use right now, and `source` says which layer
 *  won: `"db"` (the row), `"env"` (`OPENROUTER_MODEL` on the operator/
 *  support Deployments) or `"default"` (the compiled-in fallback).
 *  Render `effective_value`, never re-derive it — the resolution order
 *  is tested server-side and must not be duplicated here. */
export interface AdminPlatformSetting {
  key: string;
  value: string | null;
  effective_value: string;
  source: string;
  updated_by_sub: string | null;
  updated_at_ms: number | null;
}

export function getPlatformSettings(): Promise<{ settings: AdminPlatformSetting[] }> {
  return request('/api/v1/admin/settings');
}

/**
 * `value: null` is a deliberate CLEAR — it deletes the override so the
 * environment default applies again. It is not "leave unchanged";
 * there is no partial update on a single-value setting.
 */
export function updatePlatformSetting(
  key: string,
  body: { value: string | null },
): Promise<AdminPlatformSetting> {
  return request(`/api/v1/admin/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// --- Per-site settings (backend `site_settings.py`, migration 0036) --------------

/** `task_long_running`: alert when a task has been in progress longer than
 *  its type's limit. `by_category` holds only the types that differ from
 *  `default_minutes`; 0 means that type is never timed. */
export interface TaskLongRunningSetting {
  enabled: boolean;
  default_minutes: number;
  by_category: Record<string, number>;
}

/** One key at one site. `value` is what the site runs on now: the stored
 *  row's, or the key's default when `is_default`. */
export interface AdminSiteSetting {
  key: string;
  value: TaskLongRunningSetting;
  is_default: boolean;
  updated_by_sub: string | null;
  updated_at_ms: number | null;
}

export interface AdminSiteSettings {
  site_id: string;
  settings: AdminSiteSetting[];
  /** The task types `by_category` accepts, in the server's order. */
  task_categories: string[];
}

export const TASK_LONG_RUNNING_KEY = 'task_long_running';

export function listSiteSettings(siteId: string): Promise<AdminSiteSettings> {
  return request(`/api/v1/admin/sites/${encodeURIComponent(siteId)}/settings`);
}

export const LIFT_RULES_KEY = 'lift_rules';

export interface AdminLiftRuleBlock {
  lift: string;
  scope: 'use' | 'between_floors';
  until_kind: 'time' | 'released' | 'next_change';
  until_ms?: number | null;
  reason?: string | null;
  set_by_email?: string | null;
}

/** The Lift rules section: the switch, whether it may come on, and what turning
 *  it off would remove. */
export interface AdminLiftRulesStatus {
  site_id: string;
  enabled: boolean;
  updated_by_sub?: string | null;
  updated_at_ms?: number | null;
  /** Since an adapter was heard; null for never. */
  heard_age_s: number | null;
  reporting: boolean;
  remote_intent: boolean;
  lifts: string[];
  ready: boolean;
  blocks: AdminLiftRuleBlock[];
  calendar_rules: number;
}

export function getSiteLiftRules(siteId: string): Promise<AdminLiftRulesStatus> {
  return request(`/api/v1/admin/sites/${encodeURIComponent(siteId)}/lift-rules`);
}

/** Turning on is refused (409 `lift_rules_not_ready`) until the checks pass.
 *  Turning off with blocks needs `confirmRemoveBlocks`, or 409
 *  `lift_rule_blocks_active`. */
export function setSiteLiftRules(
  siteId: string,
  enabled: boolean,
  confirmRemoveBlocks = false,
): Promise<unknown> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(siteId)}/settings/${LIFT_RULES_KEY}`,
    {
      method: 'PUT',
      body: JSON.stringify({ value: { enabled }, confirm_remove_blocks: confirmRemoveBlocks }),
    },
  );
}

/** `value: null` reverts the key to its default (deletes the row). */
export function updateSiteSetting(
  siteId: string,
  key: string,
  value: TaskLongRunningSetting | null,
): Promise<AdminSiteSetting> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(siteId)}/settings/${encodeURIComponent(key)}`,
    { method: 'PUT', body: JSON.stringify({ value }) },
  );
}
